import React, { useState, useEffect, useMemo } from 'react';
import { formatEuro, formatEuroCompact, formatNumber as formatFixedNumber } from '../../utils/currency';
import { formatShortDate } from '../../utils/date';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Skeleton,
  Paper,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PublicIcon from '@mui/icons-material/Public';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getStocks, StockDto } from '../../services/stocksApi';
import PageHeader from '../../components/layout/PageHeader';
import AutoGrid from '../../components/layout/AutoGrid';

interface MarketIndex {
  name: string;
  symbol: string;
  value: number;
  change: number;
  changePercent: number;
  trend: 'up' | 'down' | 'neutral';
}

interface SectorPerformance {
  sector: string;
  performance: number;
  marketCap: number;
  volume: number;
  topGainers: string[];
  topLosers: string[];
}

interface MarketIndicator {
  name: string;
  value: number;
  status: 'bullish' | 'bearish' | 'neutral';
  description: string;
  unit?: string;
}

interface MarketTrendData {
  date: string;
  sp500: number;
  nasdaq: number;
  dow: number;
  volume: number;
  vix: number;
}

// Removed unused COLORS to reduce unused variables

const MarketTrendsPage: React.FC = () => {
  const [stocks, setStocks] = useState<StockDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState<'1D' | '1W' | '1M' | '3M' | '1Y'>('1M');
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    loadMarketData();
  }, []);

  const loadMarketData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const allStocks = await getStocks();
      setStocks(allStocks);
      
    } catch (err) {
      console.error('Error loading market data:', err);
      setError('Não foi possível carregar os dados de mercado');
    } finally {
      setLoading(false);
    }
  };

  // Mock market indices data
  const marketIndices = useMemo((): MarketIndex[] => {
    return [
      {
        name: 'S&P 500',
        symbol: 'SPX',
        value: 4521.45,
        change: 23.67,
        changePercent: 0.53,
        trend: 'up'
      },
      {
        name: 'NASDAQ',
        symbol: 'IXIC',
        value: 14156.78,
        change: -45.23,
        changePercent: -0.32,
        trend: 'down'
      },
      {
        name: 'Dow Jones',
        symbol: 'DJI',
        value: 35234.12,
        change: 156.89,
        changePercent: 0.45,
        trend: 'up'
      },
      {
        name: 'Russell 2000',
        symbol: 'RUT',
        value: 2134.56,
        change: -8.45,
        changePercent: -0.39,
        trend: 'down'
      },
      {
        name: 'VIX',
        symbol: 'VIX',
        value: 18.45,
        change: 1.23,
        changePercent: 7.15,
        trend: 'up'
      }
    ];
  }, []);

  // Mock sector performance data
  const sectorPerformance = useMemo((): SectorPerformance[] => {
    return [
      {
        sector: 'Tecnologia',
        performance: 2.45,
        marketCap: 12500000000000,
        volume: 8500000000,
        topGainers: ['AAPL', 'MSFT', 'GOOGL'],
        topLosers: ['NFLX', 'PYPL']
      },
      {
        sector: 'Saúde',
        performance: 1.23,
        marketCap: 6200000000000,
        volume: 3200000000,
        topGainers: ['JNJ', 'PFE'],
        topLosers: ['MRNA', 'BNTX']
      },
      {
        sector: 'Serviços financeiros',
        performance: -0.87,
        marketCap: 8900000000000,
        volume: 4100000000,
        topGainers: ['JPM', 'GS'],
        topLosers: ['BAC', 'WFC']
      },
      {
        sector: 'Energia',
        performance: 3.21,
        marketCap: 1800000000000,
        volume: 2800000000,
        topGainers: ['XOM', 'CVX'],
        topLosers: ['SLB', 'HAL']
      },
      {
        sector: 'Consumo cíclico',
        performance: 1.56,
        marketCap: 4300000000000,
        volume: 3600000000,
        topGainers: ['AMZN', 'TSLA'],
        topLosers: ['HD', 'NKE']
      }
    ].sort((a, b) => b.performance - a.performance);
  }, []);

  // Mock market indicators
  const marketIndicators = useMemo((): MarketIndicator[] => {
    return [
      {
        name: 'Rácio P/E (S&P 500)',
        value: 21.5,
        status: 'neutral',
        description: 'A avaliação do mercado está próxima da média histórica'
      },
      {
        name: 'Índice de medo e ganância',
        value: 45,
        status: 'neutral',
        description: 'O sentimento do mercado está equilibrado'
      },
      {
        name: 'Rendimento das obrigações (10 anos)',
        value: 4.25,
        status: 'bearish',
        unit: '%',
        description: 'O aumento dos rendimentos pode pressionar a valorização das ações'
      },
      {
        name: 'Índice do dólar (DXY)',
        value: 103.45,
        status: 'bullish',
        description: 'Um dólar forte pode afetar as ações internacionais'
      },
      {
        name: 'Preço do petróleo (WTI)',
        value: 82.15,
        status: 'neutral',
        description: 'Os preços do petróleo mantêm-se num intervalo normal'
      }
    ];
  }, []);

  // Generate mock trend data
  const trendData = useMemo((): MarketTrendData[] => {
    const days = timeframe === '1D' ? 1 : timeframe === '1W' ? 7 : 
                  timeframe === '1M' ? 30 : timeframe === '3M' ? 90 : 365;
    
    const data = [];
    const currentDate = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(currentDate);
      date.setDate(date.getDate() - i);
      
      // Mock trend calculations
      const baseValue = 4500;
      const trend = 1 + (Math.random() * 0.04 - 0.02); // -2% to +2% daily variation
      
      data.push({
        date: formatShortDate(date),
        sp500: baseValue * Math.pow(trend, days - i),
        nasdaq: 14000 * Math.pow(trend * 1.1, days - i),
        dow: 35000 * Math.pow(trend * 0.9, days - i),
        volume: 3000000000 + Math.random() * 2000000000,
        vix: 15 + Math.random() * 10
      });
    }
    
    return data;
  }, [timeframe]);

  const formatCurrency = (amount: number): string => {
    if (amount >= 1e6) {
      return formatEuroCompact(amount);
    }
    return formatEuro(amount);
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('pt-PT').format(num);
  };

  const formatPercentage = (percentage: number): string => {
    return `${percentage >= 0 ? '+' : ''}${formatFixedNumber(percentage)}%`;
  };

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Tendências de mercado"
        subtitle="Acompanhe o desempenho do mercado, as tendências por setor e os principais indicadores"
        actions={
          <>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Período</InputLabel>
              <Select
                value={timeframe}
                label="Período"
                onChange={(e) => setTimeframe(e.target.value as any)}
              >
                <MenuItem value="1D">1 dia</MenuItem>
                <MenuItem value="1W">1 semana</MenuItem>
                <MenuItem value="1M">1 mês</MenuItem>
                <MenuItem value="3M">3 meses</MenuItem>
                <MenuItem value="1Y">1 ano</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadMarketData}
            >
              Atualizar
            </Button>
          </>
        }
      />

      {/* Market Indices Overview */}
      <AutoGrid min={200} sx={{ mb: 3 }}>
        {marketIndices.map((index) => (
          <Card key={index.symbol}>
            <CardContent sx={{ pb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {index.trend === 'up' ? (
                  <TrendingUpIcon sx={{ color: 'success.main', mr: 1 }} />
                ) : index.trend === 'down' ? (
                  <TrendingDownIcon sx={{ color: 'error.main', mr: 1 }} />
                ) : (
                  <ShowChartIcon sx={{ color: 'text.secondary', mr: 1 }} />
                )}
                <Typography variant="subtitle2" color="text.secondary">
                  {index.name}
                </Typography>
              </Box>
              {loading ? (
                <Box>
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="text" width="40%" height={20} />
                </Box>
              ) : (
                <Box>
                  <Typography variant="h6" component="div">
                    {formatNumber(index.value)}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color={index.changePercent >= 0 ? 'success.main' : 'error.main'}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    {formatPercentage(index.changePercent)} ({index.change >= 0 ? '+' : ''}{formatFixedNumber(index.change)})
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </AutoGrid>

      {/* Tabs for different views */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} variant="scrollable" scrollButtons="auto">
          <Tab label="Visão geral do mercado" />
          <Tab label="Desempenho por setor" />
          <Tab label="Indicadores de mercado" />
          <Tab label="Análise técnica" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          {/* Market Trend Chart */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Desempenho dos índices de mercado
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={400} />
            ) : (
              <Box sx={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatNumber(Number(value))} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="sp500" 
                      stroke="#1976d2" 
                      strokeWidth={2}
                      name="S&P 500"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="nasdaq" 
                      stroke="#4caf50" 
                      strokeWidth={2}
                      name="NASDAQ"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="dow" 
                      stroke="#ff9800" 
                      strokeWidth={2}
                      name="Dow Jones"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>

          {/* Volume and VIX */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Volume e volatilidade
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={300} />
            ) : (
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip formatter={(value) => formatNumber(Number(value))} />
                    <Legend />
                    <Area 
                      yAxisId="left"
                      type="monotone" 
                      dataKey="volume" 
                      stackId="1" 
                      stroke="#1976d2" 
                      fill="#1976d2" 
                      fillOpacity={0.6}
                      name="Volume"
                    />
                    <Line 
                      yAxisId="right"
                      type="monotone" 
                      dataKey="vix" 
                      stroke="#f44336" 
                      strokeWidth={2}
                      name="VIX"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          {/* Sector Performance Chart */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Desempenho dos setores hoje
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={400} />
            ) : (
              <Box sx={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sectorPerformance} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="sector" type="category" width={100} />
                    <Tooltip formatter={(value) => `${formatFixedNumber(Number(value))}%`} />
                    <Bar 
                      dataKey="performance" 
                      fill="#1976d2"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>

          {/* Sector Details Table */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Análise por setor
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Setor</TableCell>
                    <TableCell align="right">Desempenho</TableCell>
                    <TableCell align="right">Capitalização bolsista</TableCell>
                    <TableCell align="right">Volume</TableCell>
                    <TableCell>Maiores subidas</TableCell>
                    <TableCell>Maiores descidas</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <TableRow key={index}>
                        {Array.from({ length: 6 }).map((_, cellIndex) => (
                          <TableCell key={cellIndex}><Skeleton /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    sectorPerformance.map((sector) => (
                      <TableRow key={sector.sector} hover>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {sector.sector}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            {sector.performance >= 0 ? (
                              <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                            ) : (
                              <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />
                            )}
                            <Typography 
                              color={sector.performance >= 0 ? 'success.main' : 'error.main'}
                              fontWeight="bold"
                            >
                              {formatPercentage(sector.performance)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(sector.marketCap)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(sector.volume)}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {sector.topGainers.map(stock => (
                              <Chip key={stock} label={stock} size="small" color="success" variant="outlined" />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {sector.topLosers.map(stock => (
                              <Chip key={stock} label={stock} size="small" color="error" variant="outlined" />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          {/* Market Indicators */}
          <AutoGrid min={300} gap={3}>
            {marketIndicators.map((indicator, index) => (
              <Card key={indicator.name}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AssessmentIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      {indicator.name}
                    </Typography>
                  </Box>
                  {loading ? (
                    <Box>
                      <Skeleton variant="text" width="40%" height={32} />
                      <Skeleton variant="text" width="80%" height={20} />
                      <Skeleton variant="rectangular" width={80} height={24} sx={{ mt: 1 }} />
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="h5" component="div" gutterBottom>
                        {formatNumber(indicator.value)}
                        {indicator.unit ?? ''}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {indicator.description}
                      </Typography>
                      <Chip 
                        label={indicator.status === 'bullish' ? 'ALTISTA' : indicator.status === 'bearish' ? 'BAIXISTA' : 'NEUTRO'}
                        size="small"
                        color={
                          indicator.status === 'bullish' ? 'success' : 
                          indicator.status === 'bearish' ? 'error' : 'default'
                        }
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
          </AutoGrid>
        </Box>
      )}

      {activeTab === 3 && (
        <Box>
          {/* Technical Analysis placeholder */}
          <Paper sx={{ p: 3, textAlign: 'center', minHeight: 400 }}>
            <PublicIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Análise técnica em breve
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Indicadores técnicos avançados, padrões gráficos e sinais de negociação ficarão disponíveis aqui.
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default MarketTrendsPage;
